import { useEffect, useRef } from 'react';
import { PropertyFilterProps } from '@cloudscape-design/components';
import { useFilterPersistence } from './useFilterPersistence';

interface UseCollectionFilterPersistenceOptions {
  storageKey: string;
  defaultFilters: PropertyFilterProps.Query;
  excludeFields?: string[];
  businessUnitId: string; // Required for BU-scoped keys
}

interface UseCollectionFilterPersistenceReturn {
  initialQuery: PropertyFilterProps.Query;
  syncFilters: (propertyFilterProps: {
    query: PropertyFilterProps.Query;
    onChange?: (event: { detail: PropertyFilterProps.Query }) => void;
  }) => void;
}

/**
 * Hook for persisting Cloudscape useCollection filters with sync on page reload
 *
 * This hook bridges the gap between useFilterPersistence and useCollection's
 * propertyFilterProps. It handles the complexity of syncing filter state
 * between localStorage and useCollection's internal state.
 *
 * @param options Configuration options
 * @returns Object with initialQuery for defaultQuery and syncFilters function
 *
 * @example
 * ```tsx
 * const businessUnitId = getUserBusinessUnitId();
 * const { initialQuery, syncFilters } = useCollectionFilterPersistence({
 *   storageKey: getPageFilterKey(businessUnitId, 'mypage'),
 *   defaultFilters: { tokens: [], operation: 'and' },
 *   businessUnitId,
 * });
 *
 * const { propertyFilterProps } = useCollection(items, {
 *   propertyFiltering: {
 *     filteringProperties: [...],
 *     defaultQuery: initialQuery,
 *   },
 * });
 *
 * // Call syncFilters after useCollection (this doesn't cause re-renders)
 * syncFilters(propertyFilterProps);
 * ```
 */
export function useCollectionFilterPersistence({
  storageKey,
  defaultFilters,
  excludeFields = [],
  businessUnitId,
}: UseCollectionFilterPersistenceOptions): UseCollectionFilterPersistenceReturn {
  // Use filter persistence hook
  const { filters: persistedQuery, setFilters: setPersistedQuery } = useFilterPersistence(
    {
      storageKey,
      defaultFilters,
      excludeFields,
      businessUnitId,
    },
  );

  // Store the initial persisted query to use as defaultQuery (only on mount)
  const initialQueryRef = useRef(persistedQuery);
  const hasInitialized = useRef(false);
  const lastPersistedQueryRef = useRef<string>('');
  const propertyFilterPropsRef = useRef<{
    query: PropertyFilterProps.Query;
    onChange?: (event: { detail: PropertyFilterProps.Query }) => void;
  } | null>(null);

  // Sync function to be called after useCollection
  // This updates the ref without causing re-renders
  const syncFilters = (propertyFilterProps: {
    query: PropertyFilterProps.Query;
    onChange?: (event: { detail: PropertyFilterProps.Query }) => void;
  }) => {
    propertyFilterPropsRef.current = propertyFilterProps;

    // Persist filter changes to localStorage
    const queryStr = JSON.stringify(propertyFilterProps.query);
    if (queryStr !== lastPersistedQueryRef.current) {
      lastPersistedQueryRef.current = queryStr;
      setPersistedQuery(propertyFilterProps.query as any);
      hasInitialized.current = true;
    }
  };

  // Apply filter changes from useFilterPersistence hook on page reload
  // This only runs when persistedQuery changes (from storage on page reload)
  useEffect(() => {
    const propertyFilterProps = propertyFilterPropsRef.current;
    if (!propertyFilterProps || !hasInitialized.current) return;

    const currentQueryStr = JSON.stringify(propertyFilterProps.query);
    const persistedQueryStr = JSON.stringify(persistedQuery);

    // Only apply if persistedQuery is different from current query
    if (currentQueryStr !== persistedQueryStr && propertyFilterProps.onChange) {
      // Update ref to prevent the persist effect from running again
      lastPersistedQueryRef.current = persistedQueryStr;
      propertyFilterProps.onChange({ detail: persistedQuery } as any);
    }
  }, [persistedQuery]);

  return {
    initialQuery: initialQueryRef.current,
    syncFilters,
  };
}
